import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClaimBuilder, { ClaimBuilderHandle } from '../../modules/layout/components/claimBuilder/ClaimBuilder';
import { v4 as uuidv4 } from 'uuid';
import { IAxiosGetResponse, IClaimConditionNodeDto, ILogicTypeEnum, IServiceDto, 
    ITransactionRouteDto } from '../../interfaces/IUtilities';
import { AxiosResponse } from 'axios';
import { getById, create, deleteById } from '../_request';
import Loader from '../../modules/layout/components/Loader';
import { useToast } from '../../modules/layout/components/ToastContext';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import { useTranslation } from 'react-i18next';
import BackButton from '../../modules/layout/components/BackButton';
import ConfirmModal from '../../modules/layout/components/ConfirmModal';


const ClaimForm = () => {
    const { t } = useTranslation();
    const { serviceid, id } = useParams<{ serviceid: string; id: string }>();
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();

    const rootItem: IClaimConditionNodeDto = {
        id: uuidv4(),
        transactionId: id!,
        parentId: null,
        logicType: ILogicTypeEnum.LEAF,
        claimId: null,
        orderIndex: 1,
        negated: false,
        customValue: null,
        children: []
    };
    
    const navigate = useNavigate();
    const [rootNode, setRootNode] = useState(rootItem);
    const [service, setService] = useState({} as IServiceDto);
    const [transaction, setTransaction] = useState({} as ITransactionRouteDto);
    const [isEdit, setIsEdit] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const fetchServiceData = async () => {
        try {
        let result = await getById("/api/services/", serviceid!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
            setService(result.data as IServiceDto);
            setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else {
            showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate(`/services/${serviceid}/transactions/list`);
        }
    };

    const fetchTransactionData = async () => {
        try {
        let result = await getById("/api/transactionroutes/", id!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
            setTransaction(result.data as ITransactionRouteDto);
            setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else {
            showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate(`/services/${serviceid}/transactions/list`);
        }
    };

    const fetchClaimLogicData = async () => {
        try {
            let result = await getById("/api/claims/claims-logic/", id!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setIsEdit(true);
                setRootNode(result.data as IClaimConditionNodeDto);
                setLoading(false);
            }
            else if (respStatus === 204) {
                setLoading(false);
            }
            else if (respStatus === 403) {
                navigate("/error/403");
            }
            else if (respStatus === 401) {
                navigate("/auth/login");
            }
            else if (respStatus === 500) {
                navigate("/error/500");
            }
            else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', { error: String(error) }));
            showToast(t('ConnectionError', { error: String(error) }), 'error');
            navigate(`/services/${serviceid}/transactions/list`);
        }
    };

    useEffect(() => {
        const fetchAll = async () => { 
            await Promise.all([fetchServiceData(), fetchTransactionData(), fetchClaimLogicData()]);
        };
        fetchAll();
    }, []);

    const builderRef = useRef<ClaimBuilderHandle>(null);
   
    const handleSave = async () => {
        const isValid = await builderRef.current?.validate();
        if (!isValid) {
            showToast(t('PleaseFixValidationErrors'), 'error');
            return;
        }

        setLoading(true);
        try {
            let result = await create(`/api/claims/claims-logic/${id}`, rootNode) as AxiosResponse;
            let respStatus = result.status;

            if (respStatus === 200) {
                setLoading(false);
                showToast(t('ClaimLogicAddSuccess', { transactionLabel: String(transaction.routeLabel) }), 'success');
                navigate(`/services/${serviceid}/transactions/list`);
            }
            else if (respStatus === 403) {
                navigate('/error/403');
            }
            else if(respStatus === 401){
                navigate('/auth/login');
            }
            else if (respStatus === 500) {
                navigate('/error/500');
            }
            else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            showToast(t('ConnectionError', { error: String(error) }), 'error');
        }
    };

    const handleDelete = () => {      
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            let result = await deleteById("/api/claims", id!) as AxiosResponse;
            let respStatus = result.status;

            if (respStatus === 200) {
                setLoading(false);
                showToast(t('ClaimLogicDeleteSuccess', { transactionLabel: String(transaction.routeLabel) }), 'success');
                setIsEdit(false);
                setRootNode(rootItem);
                navigate(`/services/${serviceid}/transactions/list`);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            showToast(t('ConnectionError', { error: String(error) }), 'error');
        }

        setShowModal(false);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
    };


    return (
        <div className="container mt-4 gg-container-form">

            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: "/" },
                    { title: t('Services'), href: "/services/list" },
                    { title: `${ service ? service.name : "" }: ${t('TransactionRoutes')}`, href: `/services/${serviceid}/transactions/list` },
                    { title: `${ transaction ? transaction.routeLabel : "" }: ${ isEdit ? t('Edit') : t('Create') } ${t('ClaimLogicBuilder')}` }  // Active page (no href)
                ]}/>

            {loading && (<Loader />)}

            <ClaimBuilder ref={builderRef} rootNode={rootNode} setRootNode={setRootNode} isEdit={isEdit} />

            <div className="row">
                <div className="col-6 d-flex align-items-end">
                    <BackButton goTo={`/services/${serviceid}/transactions/list`} />
                </div>

                <div className="col-6 d-flex justify-content-end align-items-end gap-2">
                    {isEdit && <button className="btn btn-danger" onClick={handleDelete}>
                        {t('Delete')}
                    </button> }
                    <button className="btn gg-btn-black" onClick={handleSave}>
                        {t('Save')}
                    </button>
                </div>  
            </div>

            <ConfirmModal
                show={showModal}
                title={t('Delete')}
                message={t('DeleteItem')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={loading}
            />
        </div>
  );

};

export default ClaimForm;
